import type { Customer } from "../customer";
import type { JsonObject } from "../json";

type CoachInsightsSummary = {
  hasAnalysis: boolean;
  overallScore?: number;
  sentimentScore?: number;
  playbookAdherenceScore?: number | null;
  significanceLevel?: "trivial" | "medium" | "high";
  significanceRationale?: string;
  isBookable?: boolean;
  isBookableRationale?: string;
};

/**
 * Per-call cost breakdown in Vapi's `CostBreakdown` shape. The backend persists
 * this in one shape for every runtime — Vapi populates it directly, and the
 * geminiTelnyx (speech-to-speech) runtime computes an equivalent at call
 * finalization (SD-9003) — so cost rendering reads a single model regardless of
 * which stack placed the call. All monetary fields are USD. Absent when no cost
 * has been computed yet (e.g. a call that has not finalized).
 */
export type CallCostBreakdown = {
  /**
   * Language-model cost. On the geminiTelnyx runtime the single
   * speech-to-speech model does the STT + LLM + TTS work, so its cost lands
   * here and `stt`/`tts` are 0.
   */
  llm: number;
  llmCompletionTokens: number;
  llmPromptTokens: number;
  /** Speech-to-text cost. 0 on the speech-to-speech geminiTelnyx runtime. */
  stt: number;
  /** Sum of all cost components. */
  total: number;
  /** Telephony/carrier transport cost. */
  transport: number;
  /** Text-to-speech cost. 0 on the speech-to-speech geminiTelnyx runtime. */
  tts: number;
  ttsCharacters: number;
  /**
   * Platform fee. On the geminiTelnyx runtime this carries the compute
   * (Fargate) amortization mapped to the same slot Vapi uses for its fee.
   */
  vapi: number;
};

type ConversationRoutingType = "agent" | "playbook";

type ConversationPlaybookAgent = {
  agentId?: string | null;
  agentName?: string | null;
};

type ConversationPlaybookHandoffEvent = {
  fromAgentId?: string | null;
  fromAgentName?: string | null;
  toAgentId: string;
  toAgentName?: string | null;
  timestamp: string;
  secondsFromStart?: number | null;
  reason?: string | null;
  source: string;
};

export type ConversationMessageAgentAttribution = {
  messageIndex: number;
  secondsFromStart?: number | null;
  role: string;
  agentId?: string | null;
  agentName?: string | null;
  handoffIndex?: number | null;
};

type ConversationPlaybookTimeline = {
  playbookId?: string | null;
  playbookTitle?: string | null;
  playbookTag?: string | null;
  squadName?: string | null;
  startAgent?: ConversationPlaybookAgent | null;
  currentAgent?: ConversationPlaybookAgent | null;
  handoffs: ConversationPlaybookHandoffEvent[];
  messageAgentAttributions: ConversationMessageAgentAttribution[];
};

/**
 * Derived per-call disposition on the wire (snake_case), the classification
 * roll-up (C7 / SD-10736). Served under the `dispositionV2` wire key; read only
 * when the classification flag is on. Mirrors dale's `Disposition` enum
 * (`classification/disposition.py`).
 *
 * `qualified` = `win` ∪ `unresolved`. The two support buckets are
 * non-opportunity calls that sit outside that denominator so they can't dilute
 * booking rate: `support_resolved` = the issue was handled in-call (or via a
 * completed human handoff); `support_unresolved` = a support call with a
 * callback/lead still owed.
 *
 * This is never a free choice on a write — dale derives it from `callLabels`
 * and rejects a `PATCH /conversations/{id}/classification` whose `dispositionV2`
 * doesn't match `derive_disposition(callLabels, company.win_outcomes)`.
 */
export const DISPOSITION_V2_VALUES = [
  "win",
  "unresolved",
  "unqualified",
  "support_resolved",
  "support_unresolved",
  "no_contact",
  "unclassified",
] as const;

export type DispositionV2 = (typeof DISPOSITION_V2_VALUES)[number];

/** How a call's disposition was set. Defaults `auto`; `manual` after an override. */
export type DispositionSource = "auto" | "manual";

/**
 * Dimension 1 — what happened on the call (exactly one). Mirrors dale's
 * `CallOutcome` (`classification/labels.py`). Note `scheduled`/`requested_appointment`
 * intentionally invert the legacy `customer.status` `booked`/`scheduled` meanings.
 */
export type CallOutcome =
  | "scheduled"
  | "estimate_scheduled"
  | "requested_appointment"
  | "lead"
  | "transferred"
  | "rescheduled"
  | "callback_scheduled"
  | "requested_callback"
  | "not_interested"
  | "support"
  | "voicemail"
  | "no_engagement"
  | "undetermined";

/**
 * Super-only transfer connection result (SD-15710). Tells whether a transfer
 * actually connected to a human vs. rang out unanswered. Null for non-super
 * viewers and non-transferred calls — render only when present.
 */
type TransferOutcome =
  | "connected"
  | "no_answer"
  | "busy"
  | "failed"
  | "unknown";

/**
 * Dimension 2 — why the caller called (zero or one). Mirrors dale's `CallDriver`.
 *
 * Urgency is deliberately NOT on this axis — it rides the orthogonal
 * `CallLabels.emergency` flag, which co-occurs with any driver.
 */
export type CallDriver =
  | "new_service"
  | "quote_estimate"
  | "appointment_status"
  | "appointment_confirmation"
  | "billing_payment"
  | "warranty_callback"
  | "membership"
  | "follow_up"
  | "general_inquiry"
  | "support_other"
  | "cancellation"
  | "reschedule";

/**
 * Dimension 3 — who is calling (zero or one; `customer` is the default). Mirrors
 * dale's `CallerType`; named `CallerIdentity` here to avoid colliding with the
 * Reports `CallerType` (`new`/`returning`).
 */
export type CallerIdentity =
  | "customer"
  | "insurance"
  | "third_party"
  | "technician"
  | "solicitor";

/**
 * Dimension 4 — whether the caller is a new or returning customer of the
 * business. `unknown` means the classifier had no durable CRM or customer
 * history signal.
 */
export type CustomerTenure = "new" | "returning" | "unknown";

/**
 * Dimension 5 — the kind of service discussed (zero or one). Mirrors dale's
 * `ServiceType`; named `CallServiceType` here to avoid colliding with the
 * `ServiceType` already exported through `@types`.
 */
export type CallServiceType =
  | "repair"
  | "maintenance"
  | "installation"
  | "diagnostic"
  | "tune_up"
  | "general"
  | "other";

/** Dimension 6 — serviceability gate (zero or more). Mirrors dale's `CallDisqualifier`. */
export type CallDisqualifier =
  | "out_of_service_area"
  | "service_not_offered"
  | "not_the_owner"
  | "wrong_number"
  | "sales_solicitation"
  | "info_only"
  | "already_has_appointment"
  | "unqualified_other";

/**
 * Dimension 7 — drill-down (zero or one) assigned only when the derived
 * disposition is `unresolved`. Mirrors dale's `UnresolvedReason`.
 */
export type UnresolvedReason =
  | "pricing"
  | "availability"
  | "price_shopper"
  | "language_barrier"
  | "technical_limitation"
  | "requested_human_agent"
  | "abandoned"
  | "unintended_disconnect"
  | "no_availability_offered"
  | "caller_deferred"
  | "information_incomplete"
  | "unresolved_other";

/** Driver vocabulary on `callLabels`. Identical to `CallDriver` — see its note on urgency. */
export type ConversationCallDriver = CallDriver;

/**
 * The classification label set carried under the `callLabels` wire key
 * (C7 / SD-10736). Mirrors dale's `LabelSet`
 * (`classification/disposition.py`), camelCase per `BaseModelConfigured`
 * (`to_camel`, `by_alias=True`).
 *
 * Every axis is a scalar except `disqualifiers`, the one genuinely multi-valued
 * dimension (a call can trip several serviceability gates). `emergency`,
 * `schedulingUrgency`, `notInterested` and `hasExistingAppointment` are
 * orthogonal boolean flags that co-occur with any driver; only `emergency`
 * affects the derived disposition — the other three are informational.
 *
 * `unresolvedReason` is gated on the derived disposition being `unresolved`:
 * dale drops an orphaned one on read, and the classification write endpoint
 * rejects one outright (422).
 */
export type CallLabels = {
  outcome: CallOutcome;
  driver?: ConversationCallDriver | null;
  callerType?: CallerIdentity | null;
  customerTenure?: CustomerTenure | null;
  serviceType?: CallServiceType | null;
  disqualifiers: CallDisqualifier[];
  unresolvedReason?: UnresolvedReason | null;
  emergency?: boolean;
  /** Priority timing that is not an emergency. Informational only. */
  schedulingUrgency?: boolean;
  /** Scoped to outbound campaign calls by the classifier. Informational only. */
  notInterested?: boolean;
  /**
   * The caller already holds a standing appointment. Informational only — it
   * replaced the retired `already_has_appointment` disqualifier, which used to
   * force appointment-continuation support calls into `unqualified`.
   */
  hasExistingAppointment?: boolean;
};

/**
 * Server-derived handling label for a grouped Phone System call (SD-14240).
 * `missed` covers calls with no AI leg and no answered team leg (no-answer,
 * queue-abandoned, chain-exhausted, and voicemail-only). Canonical home for
 * the Phone System interaction contract — `libs/Conversations/types.ts`
 * re-exports these so the leg shape stays a single source of truth.
 */
export type PhoneSystemHandlingMode = "ai" | "team" | "aiAndTeam" | "missed";

export type PhoneCallOutcome =
  | "answered"
  | "missed"
  | "voicemail"
  | "abandoned";

export const PHONE_CALL_HANDLING_DISPOSITIONS = [
  "human",
  "ai",
  "missed",
  "voicemail",
  "abandoned",
  "unknown",
] as const;

export type PhoneCallHandlingDisposition =
  (typeof PHONE_CALL_HANDLING_DISPOSITIONS)[number];

type PhoneSystemInteractionLegEvent = {
  type: string;
  occurredAt: string;
};

/** One ordered AI or team leg of a grouped Phone System call. */
export type PhoneSystemInteractionLeg = {
  kind: "ai" | "team";
  conversationId: string;
  conversationType: string;
  handlerName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  summary: string | null;
  transcript: string | null;
  hasRecording: boolean;
  events: PhoneSystemInteractionLegEvent[];
};

export type Conversation = {
  _id: string;
  agentId: string;
  callId: string;
  callType: string;
  companyId: string;
  conversationType: string;
  /** Groups every call leg belonging to one Phone System interaction. */
  phoneSystemSessionId?: string | null;
  /**
   * Grouped Phone System handling metadata (SD-14243). Present only on the
   * canonical row of a call the customer read grouped via
   * `groupPhoneSystemSessions=true`; absent on every other conversation and on
   * ungrouped reads. `handlingMode`/`handlerNames` are server-derived from
   * `legs`, which carry each ordered AI/team leg's own summary and transcript.
   */
  handlingMode?: PhoneSystemHandlingMode;
  handlerNames?: string[];
  legs?: PhoneSystemInteractionLeg[];
  createdAt: string;
  customer: Customer;
  /** Canonical customer id this conversation links to (server-resolved). */
  customerId?: string | null;
  /**
   * Seat user (employee) who handled this conversation — human phone-system
   * types only (`humanInboundCall` / `humanOutboundCall` / `voicemail`),
   * SD-11663. Absent/null on AI conversations and on rows predating the
   * backend rollout.
   */
  seatUserId?: string | null;
  /** Persisted final phone-system outcome, when the call settled on Dale. */
  phoneOutcome?: PhoneCallOutcome | null;
  /** Persisted final handler classification for a settled phone-system call. */
  handlingDisposition?: PhoneCallHandlingDisposition | null;
  customerPhone: string;
  /**
   * Per-call cost breakdown (Vapi-shaped, runtime-agnostic). Surfaced on the
   * conversation detail response; omitted on lite list rows and on calls whose
   * cost has not been computed.
   */
  costBreakdown?: CallCostBreakdown | null;
  duration: number;
  endedReason: string;
  playbookId?: string | null;
  publicEndedReason?: string;
  routingType?: ConversationRoutingType;
  forwardedFrom?: string;
  /**
   * Super-only transfer connection outcome (SD-15710). Present on transferred
   * calls; null/absent for non-super viewers and non-transferred calls.
   */
  transferOutcome?: TransferOutcome | null;
  isCompleted: boolean;
  extractedInfo: ExtractedInfo;
  extractedInfoCustom?: Record<string, JsonObject>;
  messages: Message[];
  recordingUrl: string;
  status: ConversationStatus;
  summary: string;
  transcript: string;
  transcriptLanguage?: string | null;
  transcriptEn?: string | null;
  messagesEn?: Message[] | null;
  transcriptTranslationStatus?: TranscriptTranslationStatus | null;
  smsTranscript?: string;
  smsDeliveries?: SmsDelivery[];
  /**
   * Server-derived SMS delivery state, present on both the lite list rows and
   * the detail response (SD-12696). Null/absent on non-SMS conversation types —
   * it does not mean "no delivery data".
   */
  messagingStatus?: MessagingStatus | null;
  /**
   * Why a post-call follow-up SMS was not sent (BE enum value, e.g.
   * `appointment_already_booked`). Null/absent when a follow-up text was sent
   * or the decision has not run.
   */
  followUpSmsSkipReason?: string | null;
  notes?: string;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  reviewedByName?: string | null;
  firstViewedAt?: string | null;
  firstViewedByUserId?: string | null;
  firstViewedByName?: string | null;
  lastViewedAt?: string | null;
  lastViewedByUserId?: string | null;
  lastViewedByName?: string | null;
  auditEvents?: ConversationAuditEvent[];
  emailEvents?: EmailEvent[];
  coachInsights?: CoachInsightsSummary;
  /**
   * New 7-axis classification labels (C7 / SD-10736). Read only when the
   * classification flag is on; absent on un-flagged/legacy payloads.
   */
  callLabels?: CallLabels | null;
  /**
   * Per-call disposition (`win`/`unresolved`/`unqualified`/`no_contact`/
   * `support_resolved`/`support_unresolved`/`unclassified`), served under the
   * `dispositionV2` wire key.
   */
  dispositionV2?: DispositionV2 | null;
  safeMetadata?: ConversationSafeMetadata | null;
  playbookTimeline?: ConversationPlaybookTimeline | null;
  bookingWizard?: BookingWizardSessionMetadata;
};

export type BookingWizardTreePathStep = {
  questionId: string;
  answerId: string;
};

export type BookingWizardSessionAttribution = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  referrer: string | null;
  landingPageUrl: string | null;
  samedayRef: string | null;
  capturedAt: string | null;
};

export type BookingWizardSessionMetadata = {
  sessionId?: string | null;
  lastCompletedStep?: string | null;
  status?: string | null;
  attribution?: BookingWizardSessionAttribution | null;
  // SD-9189: true when the session was created via the admin Preview tab
  // (`?previewMode=full`). The panel badges these so they aren't mistaken
  // for real abandoned customer bookings. Absent/false on real sessions.
  isPreview?: boolean | null;
  failureReason?: string | null;
  stepTimestamps?: {
    contactAndAddressCompletedAt?: string | null;
    detailsCompletedAt?: string | null;
    availabilityLoadedAt?: string | null;
    slotSelectedAt?: string | null;
    bookingAttemptedAt?: string | null;
    bookedAt?: string | null;
    bookingFailedAt?: string | null;
  } | null;
  lastActivityAt?: string | null;
  customer?: {
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
    phone?: string | null;
    phoneDigits?: string | null;
    email?: string | null;
  } | null;
  request?: {
    serviceId?: string | null;
    serviceLabel?: string | null;
    visitType?: string | null;
    requestedServiceAddress?: {
      street?: string | null;
      city?: string | null;
      stateCode?: string | null;
      zipCode?: string | null;
      country?: string | null;
    } | null;
    zipCode?: string | null;
    systemAge?: string | null;
    issueDescription?: string | null;
    // SD-9074: the customer's path through the booked trade's question tree.
    // Each step pairs a question id with the chosen answer id; resolved
    // against `company.bookingWizardConfig.trades[].questions` at render
    // time. Trade-tree questions are single-choice only (SD-8778), so the
    // list is linear in customer-traversal order.
    trade?: string | null;
    treePath?: BookingWizardTreePathStep[] | null;
    additionalDetails?: string | null;
  } | null;
  consent?: Record<string, string | null> | null;
  scheduling?: {
    availabilityLoaded?: boolean | null;
    availableSpotsCount?: number | null;
    availabilityStartDate?: string | null;
    availabilityDaysOut?: number | null;
    acceptedSpotId?: string | null;
    selectedAvailabilityDate?: string | null;
    selectedSlotStartUtc?: string | null;
    selectedSlotEndUtc?: string | null;
    selectedSlotReadableDate?: string | null;
  } | null;
  serviceTitan?: {
    jobType?: {
      id?: number | null;
      name?: string | null;
    } | null;
    locationId?: number | null;
    locationName?: string | null;
    locationAddress?: string | null;
    jobId?: number | null;
    bookingResponse?: JsonObject | null;
    customer?: JsonObject | null;
  } | null;
  display?: {
    name?: string | null;
    phone?: string | null;
    conversationListStatus?: string | null;
  } | null;
};

/**
 * Derived SMS delivery state for the conversation (SD-12696), collapsed
 * server-side from `smsDeliveries[].deliveryState` on the most recent attempt.
 * Null/absent on non-SMS conversation types.
 */
export type MessagingStatus = "pending" | "sent" | "awaitingReply" | "failed";

export type SmsDeliveryState =
  | "created"
  | "submission_failed"
  | "queued"
  | "sent"
  | "delivered"
  | "sending_failed"
  | "delivery_failed"
  | "delivery_unconfirmed";

export type SmsDelivery = {
  localMessageId: string;
  body: string;
  deliveryState: SmsDeliveryState;
  telnyxMessageId: string | null;
  telnyxStatus: string | null;
  submittedAt: string | null;
  finalizedAt: string | null;
  lastEventAt: string | null;
  lastEventType: string | null;
  failureCode: string | null;
  failureReason: string | null;
};

type ConversationCampaignSafeMetadata = {
  campaignId?: string;
  audienceExecutionId?: string;
  forwardingPhoneNumber?: string | null;
  // Populated on the conversation detail response (omitted on list rows and
  // when the campaign was deleted), so treat as optional/null.
  campaignName?: string | null;
};

// Which detector(s) produced the voicemail verdict the bot acted on. Mirrors
// the backend VoicemailDetectionSource enum.
export type VoicemailDetectionSource = "gemini" | "telnyx" | "both";

// First-class voicemail outcome for an outbound call on the geminiTelnyx stack.
// Surfaced via the conversation detail response's `safeMetadata.voicemail`;
// carries the message-left vs detected-no-message split that the headline
// `leftVoicemail` status alone can't express. All fields optional/nullable —
// additive on the backend with no backfill.
export type ConversationVoicemailSafeMetadata = {
  detected?: boolean;
  messageLeft?: boolean;
  source?: VoicemailDetectionSource | null;
  geminiVerdict?: string | null;
  telnyxResult?: string | null;
  telnyxEvent?: string | null;
  detectedAt?: string | null;
};

type ConversationSafeIntegrationMetadata = {
  type?: "servicetitan" | "fieldroutes" | "housecallpro";
  jobType?: {
    id: number;
    name: string;
    businessUnitIds?: number[];
  } | null;
  jobTypeReason?: string | null;
  tags?: Array<{
    id: number | string;
    name: string;
    color?: string;
    code?: string | null;
    importance?: string | null;
  }> | null;
  tagTypeIds?: Array<number | string> | null;
  tagSelectionReason?: string | null;
  customFieldValues?: Record<string, string> | null;
  serviceType?: unknown;
  jobId?: number | string | null;
  callUpdate?: {
    campaignId?: number;
    callType?: string;
    reason?: { name: string; lead?: boolean };
  } | null;
};

export type ConversationSafeMetadata = {
  campaign?: ConversationCampaignSafeMetadata | null;
  integration?: ConversationSafeIntegrationMetadata | null;
  voicemail?: ConversationVoicemailSafeMetadata | null;
};

export type ConversationAuditEvent = {
  eventType:
    | "viewed"
    | "fieldUpdated"
    | "noteAdded"
    | "noteUpdated"
    | "noteCleared"
    | "reviewed"
    | "unreviewed";
  createdAt: string;
  userId?: string | null;
  userName?: string | null;
  description?: string | null;
};

export type EmailEvent = {
  emailId: string;
  eventType:
    | "email.sent"
    | "email.delivered"
    | "email.bounced"
    | "email.complained"
    | "email.failed"
    | "email.delivery_delayed"
    | "email.opened"
    | "email.clicked";
  toEmails: string[];
};

/**
 * Delivery state of a team-member (internal user) notification. Mirrors dale's
 * shared `EmployeeNotificationStatus` enum. `read` is email-only.
 */
export type TeamNotificationStatus =
  | "queued"
  | "sent"
  | "delivered"
  | "read"
  | "failed";

/**
 * One email/SMS alert dispatched to an internal team member for a conversation
 * (per their notification matrix). Distinct from the customer-facing
 * `emailEvents` / `smsDeliveries`. Served by `GET /conversations/{id}/
 * team-notifications` (dale SD-12048), not projected on the conversation
 * document. camelCase per dale's `BaseModelConfigured` alias generator.
 */
export type TeamNotification = {
  /** Nullable: a historical email row may not resolve to a current user. */
  recipientUserId?: string | null;
  recipientName?: string | null;
  /** Email address for `channel: "email"`, phone for `channel: "sms"`. */
  recipientContact?: string | null;
  channel: "email" | "sms";
  status: TeamNotificationStatus;
  /** The email type or customer status that matched the user's matrix. */
  triggeringStatus?: string | null;
  conversationType?: string | null;
  /** ISO 8601. */
  sentAt?: string | null;
  failureReason?: string | null;
};

export type ConversationStatus =
  | "ended"
  | "forwarding"
  | "in-progress"
  | "queued"
  | "ringing"
  | "started"
  | "stopped"
  | "verified";

type TranscriptTranslationStatus =
  | "pending"
  | "completed"
  | "failed"
  | "skipped";

export type Message = {
  role: string;
  /** Offset into the call recording; absent on Full-Duplex and string-parsed transcripts. */
  secondsFromStart?: number | null;
  message: string;
  name: string;
  args: JsonObject | null;
  result: JsonObject | string | null;
  toolId?: string;
  /**
   * Set by `parseSmsTranscript` on the unprefixed continuation lines of a
   * multi-line SMS turn. These lines are part of an earlier send, not a new SMS,
   * so delivery-record mapping skips them. Absent on structured messages.
   */
  isContinuation?: boolean;
  /**
   * Neutral speaker label set by `parseTranscriptText` for legacy transcript
   * lines with no proven caller/team mapping — bracketed `[channel_N]`/
   * `[speaker_N]` markers ("Speaker 1", "Speaker 2", …) or a single
   * unstructured block ("Transcript"). Render sites show this instead of a
   * customer/handler name to avoid false attribution (SD-14850). Absent on
   * lines with a proven caller/team mapping and on structured messages.
   */
  speakerLabel?: string;
  /**
   * Which Sameday-side party spoke this turn, set by `parseTranscriptText` from
   * the transcript's own `[Team]` / `[AI]` markers. A call the team answers can
   * be transferred to the receptionist, so both speak inside one transcript and
   * attribution has to be decided per TURN, not per conversation (SD-16869).
   *
   * Absent on customer turns, on neutral lines, and on structured messages —
   * those have no marker to read, so the render site falls back to the
   * conversation-level identity.
   */
  agentSpeaker: "ai" | "team" | null;
};

export type ExtractedInfo = {
  name?: string;
  /** AI judgment from post-call analysis: would a follow-up text help this caller. */
  shouldSendFollowUpSms?: boolean;
  appointmentTime?: string;
  customerMessage?: string;
  quote?: string;
  serviceType?: string;
  askedToTransfer?: boolean;
  phone?: string;
  isEmergency?: boolean;
  isAlreadyCustomer?: boolean;
  email?: string;
  companyName?: string;
  status?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
};
